// src/pages/Index.tsx
import Head from '@/components/layout/Head';
import PayPalCalculator from '@/components/calculator/PayPalCalculator';
import PayPalExamples from '@/components/sections/PayPalExamples';
import PayPalFAQ from '@/components/sections/PayPalFAQ';
import { ExternalLink } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';

const Home = () => {
  return (
    <>
      <Head />
      <main className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto space-y-8"> {/* Main content container */}
            {/* Calculator Section */}
            <PayPalCalculator />
            
            {/* Key Features Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-white">
                <CardContent className="p-4">
                  <h3 className="font-bold text-lg mb-2">Aktuelle Gebühren 2025</h3>
                  <p className="text-gray-600 text-sm">
                    Unser Rechner verwendet die neuesten PayPal Gebühren, die seit Januar 2025 gelten.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <h3 className="font-bold text-lg mb-2">Einfache Berechnung</h3>
                  <p className="text-gray-600 text-sm">
                    Berechnen Sie schnell und unkompliziert die anfallenden Gebühren oder die benötigte Endsumme.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <h3 className="font-bold text-lg mb-2">Alle Zahlungsarten</h3>
                  <p className="text-gray-600 text-sm">
                    Von Standard-Zahlungen bis zu speziellen Händlerkonditionen - alle Gebührenmodelle verfügbar.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Info Card Section */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>PayPal Gebührenrechner 2025</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="text-gray-600">
                    Mit unserem PayPal Gebührenrechner können Sie schnell und einfach Ihre PayPal-Gebühren berechnen. 
                    Der Rechner basiert auf den aktuellen PayPal-Gebühren vom Januar 2025 und berücksichtigt alle 
                    Zahlungsarten wie Waren und Dienstleistungen, Freunde und Familie sowie spezielle Händlerkonditionen.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Gebühren für verschiedene Zahlungsarten</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="font-medium mb-1">Standard-Transaktionen</p>
                      <div className="text-gray-600 text-sm">2,49% + 0,35€</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="font-medium mb-1">Freunde und Familie</p>
                      <div className="text-gray-600 text-sm">Kostenlos innerhalb Deutschlands</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="font-medium mb-1">QR-Code (über 10,01€)</p>
                      <div className="text-gray-600 text-sm">0,5% + 0,10€</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="font-medium mb-1">QR-Code (bis 10€)</p>
                      <div className="text-gray-600 text-sm">1% + 0,05€</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Händlerkonditionen nach Umsatz</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="font-medium mb-1">Bis 2.000€</p>
                      <div className="text-gray-600 text-sm">2,49% + 0,35€</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="font-medium mb-1">2.000€ - 5.000€</p>
                      <div className="text-gray-600 text-sm">2,19% + 0,35€</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="font-medium mb-1">5.000€ - 25.000€</p>
                      <div className="text-gray-600 text-sm">1,99% + 0,35€</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="font-medium mb-1">Über 25.000€</p>
                      <div className="text-gray-600 text-sm">1,79% + 0,35€</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Examples Section */}
            <PayPalExamples />

            {/* Official Links Section */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Offizielle PayPal Informationen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <a 
                    href="https://www.paypal.com/de/digital-wallet/paypal-consumer-fees"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">PayPal Gebührenübersicht</h3>
                      <p className="text-sm text-gray-600">Aktuelle Gebührenübersicht auf PayPal.com</p>
                    </div>
                    <ExternalLink className="h-5 w-5 text-gray-400" />
                  </a>
                  <a 
                    href="https://www.paypalobjects.com/marketing/ua/pdf/DE/de/de-de-consumer-fees-09-Jan-2025.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">Detaillierte Gebührenaufstellung</h3>
                      <p className="text-sm text-gray-600">Offizielle PayPal Gebühren als PDF (Stand: Januar 2025)</p>
                    </div>
                    <ExternalLink className="h-5 w-5 text-gray-400" />
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* FAQ Section */}
            <PayPalFAQ />
          </div>
        </div>
      </main>
    </>
  );
};

export default Home;